import useActiveNavigation from '@Guidelines/Shell/AppShell/Hooks/useActiveNavigation';
import useNavigationMenu from '@Guidelines/Shell/AppShell/Hooks/useNavigationMenu';
import useVisibleNavigationMenu from '@Guidelines/Shell/AppShell/Hooks/useVisibleNavigationMenu';
import PrimaryNavEntry from '@Guidelines/Shell/AppShell/AppHeader/PrimaryNav/PrimaryNavEntry';
import PrimaryNavSubjectsMenu from '@Guidelines/Shell/AppShell/AppHeader/PrimaryNav/PrimaryNavSubjectsMenu';
import { theme } from '@Guidelines/Theme/theme';
import type { VisibleNavigationEntry } from '@Guidelines/Shell/AppShell/Hooks/useVisibleNavigationMenu';
import useUserPermissions from '@UI/Providers/PermissionProvider/useUserPermissions';
import { css } from '@emotion/react';
import * as React from 'react';
import { Skeleton } from '@verclam/ui-kit';

const ACCOMPANIMENT_SECTION_IDS = ['accompaniment'] as const;

/** The visible menu is empty until permissions resolve: one skeleton per accompaniment entry stands in. */
const PLACEHOLDER_KEYS = ['home', 'beneficiaries', 'workflow-runs'] as const;

const SUBJECT_ENTRY_PREFIX = 'subject:';

type NavChip =
	| { readonly type: 'entry'; readonly item: VisibleNavigationEntry }
	| { readonly type: 'subjects'; readonly items: readonly VisibleNavigationEntry[] };

const isSubjectItem = (item: VisibleNavigationEntry): boolean => {
	return item.entry.id.startsWith(SUBJECT_ENTRY_PREFIX);
};

/** Keeps ordinary entries as they are and folds every dossier type into one `subjects` chip, at the first type's position. */
const groupNavChips = (items: readonly VisibleNavigationEntry[]): readonly NavChip[] => {
	const subjectItems = items.filter(isSubjectItem);

	return items.flatMap((item): readonly NavChip[] => {
		if (!isSubjectItem(item)) {
			return [{ type: 'entry', item }];
		}

		return item === subjectItems[0] ? [{ type: 'subjects', items: subjectItems }] : [];
	});
};

const navStyle = css`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	margin-left: auto;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		display: none;
	}
`;

/**
 * Desktop top-bar navigation: one chip per entry of the accompaniment section, the dossier types being
 * grouped under a single « Dossiers » menu chip. While permissions or subject types load, skeleton chips
 * hold the place of the gated entries so the row does not jump. Hidden below `layout.compactBreakpoint`,
 * where the drawer takes over.
 */
const PrimaryNav: React.FC = () => {
	const { isResolved } = useUserPermissions();
	const { isLoading: areSubjectTypesLoading } = useNavigationMenu();
	const sections = useVisibleNavigationMenu(ACCOMPANIMENT_SECTION_IDS);
	const activeNavigation = useActiveNavigation();
	const chips = React.useMemo(() => {
		return groupNavChips(sections.flatMap((section) => section.entries));
	}, [sections]);
	const isSubjectActive = activeNavigation?.entry.id.startsWith(SUBJECT_ENTRY_PREFIX) ?? false;

	return (
		<nav css={navStyle} aria-label="Navigation principale">
			{chips.map((chip) => {
				if (chip.type === 'subjects') {
					return (
						<PrimaryNavSubjectsMenu
							key="subjects"
							items={chip.items}
							isActive={isSubjectActive}
						/>
					);
				}

				return (
					<PrimaryNavEntry
						key={chip.item.entry.id}
						item={chip.item}
						isActive={activeNavigation?.entry.id === chip.item.entry.id}
					/>
				);
			})}
			{isResolved && !areSubjectTypesLoading
				? null
				: PLACEHOLDER_KEYS.map((key) => (
						<Skeleton key={key} width="6.5rem" height="2.25rem" borderRadius={theme.radii.pill} />
					))}
		</nav>
	);
};

export default PrimaryNav;
